// src/components/DocumentListScreen.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Typography,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  DescriptionOutlined,
  WifiOff,
  SyncOutlined,
  MoreVert,
  CloudDownload,
  CloudDone,
  Folder,
  KeyboardArrowRight,
  Home as HomeIcon
} from '@mui/icons-material';
import DocumentService from '../services/DocumentService';
import BandwidthLimitedDownloadService from '../services/BandwidthLimitedDownloadService';
import { storage } from '../firebase';
import { ref, listAll } from 'firebase/storage';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';

const DocumentListScreen = ({ section, onBack, onSelectDocument, onManualSync }) => {
  // Use the Material-UI theme to access palette values
  const muiTheme = useTheme();
  // Use the app's theme context to access dark mode state
  const { darkMode } = useAppTheme();

  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Get the appropriate storage path
  const getStoragePath = () => {
    if (typeof section === 'string') {
      return section.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
    }
    if (section && section.path) {
      return section.path;
    }
    if (section && section.name) {
      return section.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
    }
    return '';
  };

  // Generate breadcrumbs from path
  const generateBreadcrumbs = (path) => {
    if (!path) {
      setBreadcrumbs([]);
      return;
    }
    const parts = path.split('/');
    const crumbs = [];
    let currentPath = '';
    parts.forEach((part, index) => {
      currentPath += (index > 0 ? '/' : '') + part;
      crumbs.push({
        name: part.replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/(\d+\.\s*)/, ''),
        path: currentPath
      });
    });
    setBreadcrumbs(crumbs);
  };

  // Enhanced network check function
  const checkActualNetworkStatus = async () => {
    if (!navigator.onLine) {
      return false;
    }
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      await fetch('https://1.1.1.1', { 
        method: 'HEAD', 
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      console.log('[Network check] Failed, we are actually offline:', error);
      return false;
    }
  };

  // Fetch documents and subfolders from the given path
  const fetchDocumentsAndFolders = async (path) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Fetching from path: ${path}`);
      const actuallyOnline = await checkActualNetworkStatus();
      setIsOffline(!actuallyOnline);
      if (actuallyOnline) {
        try {
          const folderRef = ref(storage, path);
          const result = await listAll(folderRef);
          const folderList = result.prefixes.map(prefix => ({
            name: prefix.name,
            fullPath: prefix.fullPath
          }));
          setFolders(folderList);
          const docs = await DocumentService.fetchDocumentsBySection(path);
          setDocuments(docs);
        } catch (onlineError) {
          console.error('Error fetching online content:', onlineError);
          await fetchOfflineContent(path);
        }
      } else {
        await fetchOfflineContent(path);
      }
      const lastSyncTime = await DocumentService.getLastSyncStatus(path);
      setLastSync(lastSyncTime);
    } catch (err) {
      console.error('Error fetching documents and folders:', err);
      setError('Failed to load content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to fetch content in offline mode
  const fetchOfflineContent = async (path) => {
    try {
      setFolders([]);
      const docs = await DocumentService.fetchDocumentsBySection(path);
      setDocuments(docs);
      if (docs.length === 0) {
        setError('No cached documents available for this section. Please connect to the internet to download documents.');
      }
    } catch (offlineError) {
      console.error('Error fetching offline content:', offlineError);
      setError('Failed to load cached content.');
    }
  };

  // Format the file size to a readable format
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format the date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Initialize the component with the section data
  useEffect(() => {
    if (section) {
      const path = section.path || getStoragePath();
      setCurrentPath(path);
      generateBreadcrumbs(path);
      fetchDocumentsAndFolders(path);
    }
  }, [section]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (currentPath) {
        fetchDocumentsAndFolders(currentPath);
      }
    };
    const handleOffline = () => {
      setIsOffline(true);
      setFolders([]);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentPath]);

  // Queue documents for background download when available
  useEffect(() => {
    if (documents.length > 0 && navigator.onLine) {
      BandwidthLimitedDownloadService.queueSectionDocuments(currentPath, documents);
      updateOfflineStatus();
    }
  }, [documents, currentPath]);

  // Update offline availability status of documents
  const updateOfflineStatus = async () => {
    if (documents.length === 0) return;
    const updatedDocs = [...documents];
    let hasChanges = false;
    for (let i = 0; i < updatedDocs.length; i++) {
      try {
        const isCached = await DocumentService.isDocumentCached(updatedDocs[i].id);
        if (isCached && !updatedDocs[i].lastCached) {
          updatedDocs[i] = { ...updatedDocs[i], lastCached: new Date().toISOString() };
          hasChanges = true;
        } else if (!isCached && updatedDocs[i].lastCached) {
          delete updatedDocs[i].lastCached;
          hasChanges = true;
        }
      } catch (error) {
        console.error(`Error checking cache status for ${updatedDocs[i].id}:`, error);
      }
    }
    if (hasChanges) {
      setDocuments(updatedDocs);
    }
  };

  // Handle saving a document for offline use
  const handleSaveForOffline = async () => {
    if (!selectedDoc) return;
    try {
      await DocumentService.cacheDocumentForOffline(selectedDoc.id);
      updateOfflineStatus();
    } catch (error) {
      console.error('Error saving for offline:', error);
    } finally {
      handleCloseMenu();
    }
  };

  // Close the context menu
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedDoc(null);
  };

  // Handle folder click to navigate into a subfolder
  const handleFolderClick = (folder) => {
    setCurrentPath(folder.fullPath);
    generateBreadcrumbs(folder.fullPath);
    fetchDocumentsAndFolders(folder.fullPath);
  };

  // Handle back button click
  const handleBackButtonClick = () => {
    if (onBack) {
      onBack();
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    if (currentPath) {
      fetchDocumentsAndFolders(currentPath);
    }
  };

  // Component to show download status
  const DownloadStatusIndicator = ({ document }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const handleDownload = async (e) => {
      e.stopPropagation();
      setIsDownloading(true);
      try {
        await BandwidthLimitedDownloadService.downloadDocument(document);
        updateOfflineStatus();
      } catch (error) {
        console.error(`Error downloading document ${document.name}:`, error);
      } finally {
        setIsDownloading(false);
      }
    };
    if (document.lastCached) {
      return (
        <Chip icon={<CloudDone fontSize="small" />} label="Offline" size="small" color="success" sx={{ height: 24 }} />
      );
    }
    if (isDownloading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="caption">Downloading</Typography>
        </Box>
      );
    }
    return (
      <IconButton size="small" onClick={handleDownload} disabled={isOffline}>
        <CloudDownload fontSize="small" />
      </IconButton>
    );
  };

  // Get the section title to display in the header
  const getSectionTitle = () => {
    if (section && section.name) {
      return section.name;
    }
    if (breadcrumbs.length > 0) {
      const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
      return lastCrumb.name;
    }
    return 'Documents';
  };

  return (
    <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Always display the AppBar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackButtonClick}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            {getSectionTitle()}
          </Typography>
          {isOffline && (
            <Chip
              icon={<WifiOff />}
              label="Offline Mode"
              color="warning"
              size="small"
              sx={{ mr: 1 }}
            />
          )}
          <IconButton 
            color="inherit"
            onClick={isOffline ? onManualSync : handleRefresh}
            disabled={syncing}
          >
            <SyncOutlined />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Show breadcrumbs if available */}
      {breadcrumbs.length > 1 && (
        <Box sx={{ 
          px: 2, 
          py: 1, 
          backgroundColor: muiTheme.palette.background.paper 
        }}>
          <Breadcrumbs separator={<KeyboardArrowRight fontSize="small" />}>
            {breadcrumbs.map((crumb, index) => (
              <Typography
                key={index}
                variant="body2"
                color={index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary'}
              >
                {crumb.name}
              </Typography>
            ))}
          </Breadcrumbs>
        </Box>
      )}

      <Paper 
        elevation={3} 
        sx={{ 
          flexGrow: 1, 
          m: 2, 
          overflow: 'auto', 
          backgroundColor: darkMode 
            ? muiTheme.palette.background.default 
            : muiTheme.palette.background.paper
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : (
          <List>
            {/* Show folders first */}
            {folders.length > 0 && (
              <>
                {folders.map((folder) => (
                  <ListItem 
                    key={folder.fullPath} 
                    sx={{ py: 1, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                  >
                    <ListItemButton onClick={() => handleFolderClick(folder)}>
                      <ListItemIcon>
                        <Folder color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={folder.name} secondary="Folder" />
                    </ListItemButton>
                  </ListItem>
                ))}
                {documents.length > 0 && <Divider sx={{ my: 1 }} />}
              </>
            )}
            
            {/* Then show documents */}
            {documents.map((doc) => (
              <ListItem 
                key={doc.id} 
                sx={{ 
                  py: 2, 
                  '&:hover': { 
                    backgroundColor: darkMode 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)' 
                  } 
                }}
              >
                <ListItemButton onClick={() => onSelectDocument(doc.id, doc.name)}>
                  <ListItemIcon>
                    <DescriptionOutlined color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.name.replace(/\.[^/.]+$/, '')}
                    secondary={
                      <>
                        <Typography sx={{ display: 'block' }} component="span" variant="body2" color="text.primary">
                          {doc.contentType?.includes('word')
                            ? 'Word Document'
                            : doc.contentType?.includes('pdf')
                            ? 'PDF Document'
                            : doc.contentType || 'Document'}
                        </Typography>
                        <Typography sx={{ display: 'block' }} component="span" variant="body2" color="text.secondary">
                          {formatFileSize(doc.size || 0)} • Updated: {formatDate(doc.updated)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <DownloadStatusIndicator document={doc} />
                </Box>
                <IconButton 
                  edge="end" 
                  aria-label="more options" 
                  onClick={(e) => {
                    setMenuAnchorEl(e.currentTarget);
                    setSelectedDoc(doc);
                  }}
                >
                  <MoreVert />
                </IconButton>
              </ListItem>
            ))}
            
            {folders.length === 0 && documents.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {isOffline 
                    ? "No cached documents available for this section. Connect to the internet to access documents."
                    : "No files or folders found in this location"}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Paper>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem 
          onClick={handleSaveForOffline} 
          disabled={selectedDoc?.lastCached !== undefined || isOffline}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedDoc?.lastCached ? 'Already Available Offline' : 'Save for Offline Use'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DocumentListScreen;
